import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportDropdown = ({ onExport, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const exportOptions = [
    {
      id: 'best_prices',
      label: 'Best Prices Report',
      description: 'Products with lowest prices from each supplier',
      format: 'CSV',
      icon: 'Crown',
      filename: 'products_best_price.csv'
    },
    {
      id: 'all_groups',
      label: 'All Product Groups',
      description: 'Complete product matching groups with all suppliers',
      format: 'CSV',
      icon: 'Grid3x3',
      filename: 'all_groups.csv'
    },
    {
      id: 'ambiguous_pairs',
      label: 'Ambiguous Matches',
      description: 'Products requiring manual review',
      format: 'CSV',
      icon: 'AlertTriangle',
      filename: 'ambiguous_pairs.csv'
    },
    {
      id: 'unmatched',
      label: 'Unmatched Products',
      description: 'Products with no matches found',
      format: 'CSV',
      icon: 'FileX',
      filename: 'unmatched.csv'
    },
    {
      id: 'supplier_analytics',
      label: 'Supplier Performance',
      description: 'Analytics and performance metrics by supplier',
      format: 'XLSX',
      icon: 'BarChart3',
      filename: 'supplier_analytics.xlsx'
    },
    {
      id: 'full_report',
      label: 'Complete Analysis Report',
      description: 'Comprehensive report with all data and insights',
      format: 'XLSX',
      icon: 'FileSpreadsheet',
      filename: 'complete_analysis.xlsx'
    }
  ];

  const handleExport = async (option) => {
    setIsExporting(true);
    setIsOpen(false);
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (onExport) {
        onExport(option);
      }
      
      // Create and trigger download
      const blob = new Blob(['Mock export data'], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = option?.filename;
      document.body?.appendChild(a);
      a?.click();
      window.URL?.revokeObjectURL(url);
      document.body?.removeChild(a);
      
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="default"
        onClick={toggleDropdown}
        disabled={isExporting}
        loading={isExporting}
        iconName={isExporting ? 'Loader2' : 'Download'}
        iconPosition="left"
        className="min-w-[140px]"
      >
        {isExporting ? 'Exporting...' : 'Export Results'}
      </Button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-lg shadow-modal z-50 animate-fade-in">
          <div className="p-2">
            <div className="px-3 py-2 border-b border-border">
              <h4 className="text-sm font-medium text-foreground">Export Options</h4>
              <p className="text-xs text-muted-foreground">Choose the data format you need</p>
            </div>
            
            <div className="py-2 max-h-96 overflow-y-auto">
              {exportOptions?.map((option) => (
                <button
                  key={option?.id}
                  onClick={() => handleExport(option)}
                  disabled={isExporting}
                  className="w-full flex items-start space-x-3 px-3 py-3 hover:bg-muted rounded-md transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-8 h-8 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                      <Icon name={option?.icon} size={16} className="text-primary" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground truncate">
                        {option?.label}
                      </p>
                      <span className="ml-2 px-2 py-0.5 bg-muted text-xs font-medium text-muted-foreground rounded">
                        {option?.format}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {option?.description}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 font-mono">
                      {option?.filename}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="px-3 py-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                <Icon name="Info" size={12} className="inline mr-1" />
                Large exports may take a few moments to process
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ExportDropdown;